import cn from "classnames";
import { useEffect, useState } from "react";
import { Button } from "@rshbintech.dboul/core-ui";
import {
  monolithApi,
  confirmationApi,
  MonolithApiTypes,
  ConfirmationApiTypes,
  rckApi,
} from "@rshbintech.dboul/http";
import {
  cadesplugin,
  buildSignature,
  signCertificate,
} from "@rshbintech.dboul/utils";
import { useAppTranslation } from "../../hooks";
import { usePosition } from "../position-context";
import { Base64 } from "../utils";
import { Loader } from "../loader";
import { SIGNING_TYPES } from "../constants";
import { SignedDocumentTypes } from "../types";
import {
  SIGNING_BUTTON_TEXT,
  SIGNING_WITH_CERTIFICATE_STATUSES,
} from "./constants";
import { cleanLineBreaksFromSignature } from "./utils";
import {
  SigningWithCertificateProps,
  SigningWithCertificateStatuses,
} from "./types";
import styles from "./styles.scss";

export const SigningWithCertificate = ({
  buttonText,
  signingType,
  className,
  ccbVersion,
  documentId,
  signedDocument,
  certificateId,
  certificateIssuer,
  cryptoControlBlock,
  signingWithMonolith,
  onErrorSigning,
  onSuccessSigning,
  onClickSigningButton,
  disableSignButton,
  dataAttributesSigningWithCertificateButton,
  onSignatureChecked,
}: SigningWithCertificateProps) => {
  const [signature, setSignature] = useState("");
  const [signingMessage, setSigningMessage] = useState("");
  const [cryptoModuleError, setCryptoModuleError] = useState("");
  const [signingStatus, setSigningStatus] =
    useState<SigningWithCertificateStatuses>(
      SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING
    );

  const { translate } = useAppTranslation();

  const position = usePosition();

  cadesplugin();

  const signingDocument = async () => {
    if (
      !signingWithMonolith &&
      signedDocument &&
      certificateId &&
      certificateIssuer
    ) {
      try {
        let checkSignatureResponse;

        if (signingType === SIGNING_TYPES.SIGNING && ccbVersion) {
          checkSignatureResponse = await confirmationApi.api.checkSignature({
            signature,
            ccbVersion,
            document:
              signedDocument as SignedDocumentTypes["confirmationDocumentType"],
            certificate: {
              issuer: certificateIssuer,
              serial: certificateId,
            },
          });

          onSignatureChecked?.(checkSignatureResponse.data);
        } else {
          checkSignatureResponse = await rckApi.api.viseCheckSignature({
            signature,
            document: signedDocument as SignedDocumentTypes["viseDocumentType"],
            certificate: {
              issuer: certificateIssuer,
              serial: certificateId,
            },
          });
        }

        if (checkSignatureResponse.data.valid) {
          setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING);
          onSuccessSigning?.();
        } else {
          setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING);
          onErrorSigning?.(checkSignatureResponse.data.desc ?? "");
        }
      } catch (error) {
        setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING);
        onErrorSigning?.(
          (error as Record<string, ConfirmationApiTypes.ErrorMessage>)?.error
            ?.description
        ); // TODO: Изменить работу с типизацией и обработкой ошибок запросов
      }
    } else if (signingWithMonolith && documentId && certificateId) {
      try {
        await monolithApi.documents.signCreate({
          documentId: Number(documentId),
          certId: certificateId,
          signature: Base64.encode(signature),
        });

        setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING);
        onSuccessSigning?.();
      } catch (error) {
        setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING);
        onErrorSigning?.(
          (error as Record<string, MonolithApiTypes.ErrorResponse>)?.error
            ?.errors?.[0]
        ); // TODO: Изменить работу с типизацией и обработкой ошибок запросов
      }
    }
  };

  useEffect(() => {
    if (cryptoModuleError) {
      setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.BEGIN_SIGNING);
      onErrorSigning?.(cryptoModuleError);
    }
  }, [cryptoModuleError]);

  useEffect(() => {
    if (signingMessage && certificateId) {
      if (!signingWithMonolith) {
        const cleanedSignature = cleanLineBreaksFromSignature(signingMessage);

        setSignature(cleanedSignature);
      } else {
        buildSignature(certificateId, signingMessage, setSignature);
      }
    }
  }, [signingMessage]);

  useEffect(() => {
    if (signature) {
      signingDocument();
    }
  }, [signature]);

  const handleCreateSign = () => {
    setSigningStatus(SIGNING_WITH_CERTIFICATE_STATUSES.SIGNING);
    setCryptoModuleError("");
    if (certificateId) {
      signCertificate(
        certificateId,
        cryptoControlBlock,
        setSigningMessage,
        setCryptoModuleError,
        false
      );
    }

    onClickSigningButton?.();
  };

  const isSigning = signingStatus === SIGNING_WITH_CERTIFICATE_STATUSES.SIGNING;

  return (
    <div
      data-test-id="signing-with-certificate"
      className={cn(styles.container, className)}
    >
      {isSigning ? (
        <Loader dataTestId="signing-with-certificate-loader" />
      ) : (
        <Button
          disabled={disableSignButton}
          onClick={handleCreateSign}
          dataTestId="signing-button"
          className={cn(styles["signing-button"], styles[position])}
          data-rptracker-event={
            dataAttributesSigningWithCertificateButton?.dataRptrackerEvent
          }
          data-rptracker-category={
            dataAttributesSigningWithCertificateButton?.dataRptrackerCategory
          }
          data-rptracker-action={
            dataAttributesSigningWithCertificateButton?.dataRptrackerAction
          }
          data-rptracker-value={
            dataAttributesSigningWithCertificateButton?.dataRptrackerValue
          }
        >
          {buttonText ?? translate(SIGNING_BUTTON_TEXT[signingType])}
        </Button>
      )}
    </div>
  );
};
